import { useMemo } from 'react';
import {
    Truck,
    Clock,
    CheckCircle,
    XCircle,
    TrendingUp,
    BarChart3,
    MapPin,
    Store,
    Calendar,
} from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';

import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import { useFilters } from '../context/FilterContext';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatNumber, formatPercentage, formatCurrency, formatTimeSlot } from '../utils/formatters';
import { groupBy } from '../utils/calculations';
import styles from './OrderAnalytics.module.css';

// --------------------------------------------------------------
// Helpers
// --------------------------------------------------------------

/**
 * Given an order from the API, determine ON-TIME status.
 * On-time = the order was delivered on or before the delivery schedule date.
 * Late     = delivered after schedule, or delayed.
 * Cancelled = status === 'Cancelled'
 */
function getDeliveryStatus(order) {
    if (order.status === 'Cancelled') return 'Cancelled';

    if (order.deliveryDate && order.deliveryScheduleDate) {
        const actual = new Date(order.deliveryDate);
        const scheduled = new Date(order.deliveryScheduleDate);
        return actual <= scheduled ? 'On Time' : 'Late';
    }

    // Fallback: use the mapped isDelayed flag
    if (order.isDelayed) return 'Late';
    if (order.status === 'Delivered') return 'On Time';
    return 'Processing';
}

/**
 * Compute delivery time in minutes between pickUpDate and deliveryDate.
 */
function deliveryDurationMins(order) {
    if (order.pickUpDate && order.deliveryDate) {
        const pickup = new Date(order.pickUpDate);
        const delivered = new Date(order.deliveryDate);
        const diff = (delivered - pickup) / 60000; // ms → minutes
        return diff > 0 ? diff : null;
    }
    return null;
}

function buildCityStoreRows(filteredOrders, getCityName) {
    const cityGroups = groupBy(filteredOrders, 'cityId');

    return Object.entries(cityGroups)
        .map(([cityId, cityOrders]) => {
            const cityName = getCityName(cityId) || cityId;
            const delivered = cityOrders.filter(o => o.status !== 'Cancelled');
            const onTime = delivered.filter(o => getDeliveryStatus(o) === 'On Time');
            const late = delivered.filter(o => getDeliveryStatus(o) === 'Late');
            const cancelled = cityOrders.filter(o => o.status === 'Cancelled');

            const durations = cityOrders.map(o => deliveryDurationMins(o)).filter(d => d !== null);
            const avgDispatch = durations.length > 0
                ? durations.reduce((a, b) => a + b, 0) / durations.length
                : null;

            const onTimePercent = delivered.length > 0 ? (onTime.length / delivered.length) * 100 : 0;
            const latePercent = delivered.length > 0 ? (late.length / delivered.length) * 100 : 0;
            const cancelledPercent = cityOrders.length > 0 ? (cancelled.length / cityOrders.length) * 100 : 0;

            // Store-level children
            const storeGroups = groupBy(cityOrders, 'storeId');
            const children = Object.entries(storeGroups).map(([storeId, storeOrders]) => {
                const storeDelivered = storeOrders.filter(o => o.status !== 'Cancelled');
                const storeOnTime = storeDelivered.filter(o => getDeliveryStatus(o) === 'On Time');
                const storeLate = storeDelivered.filter(o => getDeliveryStatus(o) === 'Late');
                const storeCancelled = storeOrders.filter(o => o.status === 'Cancelled');
                const storeDurations = storeOrders.map(o => deliveryDurationMins(o)).filter(d => d !== null);
                const storeAvgDispatch = storeDurations.length > 0
                    ? storeDurations.reduce((a, b) => a + b, 0) / storeDurations.length
                    : null;
                return {
                    id: storeId,
                    name: storeId,
                    totalOrders: storeOrders.length,
                    onTimePercent: storeDelivered.length > 0 ? (storeOnTime.length / storeDelivered.length) * 100 : 0,
                    latePercent: storeDelivered.length > 0 ? (storeLate.length / storeDelivered.length) * 100 : 0,
                    cancelledPercent: storeOrders.length > 0 ? (storeCancelled.length / storeOrders.length) * 100 : 0,
                    avgDispatch: storeAvgDispatch ? `${Math.round(storeAvgDispatch)} min` : 'N/A',
                };
            });

            return {
                id: cityId,
                name: cityName,
                totalOrders: cityOrders.length,
                onTimePercent,
                latePercent,
                cancelledPercent,
                avgDispatch: avgDispatch ? `${Math.round(avgDispatch)} min` : 'N/A',
                children,
            };
        })
        .sort((a, b) => b.totalOrders - a.totalOrders);
}

// --------------------------------------------------------------
// Time Slot Analysis using real TimeSlotId
// --------------------------------------------------------------
function buildTimeSlotRows(filteredOrders) {
    const slotGroups = groupBy(filteredOrders, 'timeSlotId');
    return Object.entries(slotGroups)
        .filter(([slotId]) => slotId && slotId !== 'null' && slotId !== 'undefined')
        .map(([slotId, slotOrders]) => {
            const label = formatTimeSlot(slotId);
            const delivered = slotOrders.filter(o => o.status !== 'Cancelled');
            const onTime = delivered.filter(o => getDeliveryStatus(o) === 'On Time');
            const late = delivered.filter(o => getDeliveryStatus(o) === 'Late');
            const cancelled = slotOrders.filter(o => o.status === 'Cancelled');
            return {
                id: slotId,
                timeSlot: label,
                totalOrders: slotOrders.length,
                onTime: onTime.length,
                late: late.length,
                cancelled: cancelled.length,
                onTimePercent: delivered.length > 0 ? (onTime.length / delivered.length) * 100 : 0,
            };
        })
        .sort((a, b) => b.totalOrders - a.totalOrders);
}

// --------------------------------------------------------------
// Component
// --------------------------------------------------------------
function DeliveryPerformance() {
    const { appliedFilters, orders, getCityName } = useFilters();

    const filteredOrders = useMemo(() => {
        const filters = {
            startDate: appliedFilters.dateRange?.startDate
                ? appliedFilters.dateRange.startDate.toISOString().split('T')[0]
                : null,
            endDate: appliedFilters.dateRange?.endDate
                ? appliedFilters.dateRange.endDate.toISOString().split('T')[0]
                : null,
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters, orders]);

    // KPI metrics
    const metrics = useMemo(() => {
        const total = filteredOrders.length;
        const delivered = filteredOrders.filter(o => o.status !== 'Cancelled');
        const onTime = delivered.filter(o => getDeliveryStatus(o) === 'On Time');
        const late = delivered.filter(o => getDeliveryStatus(o) === 'Late');
        const cancelled = filteredOrders.filter(o => o.status === 'Cancelled');

        const durations = filteredOrders
            .map(o => deliveryDurationMins(o))
            .filter(d => d !== null);
        const avgDeliveryTime = durations.length > 0
            ? durations.reduce((a, b) => a + b, 0) / durations.length
            : null;

        const onTimeRate = delivered.length > 0 ? (onTime.length / delivered.length) * 100 : 0;

        return {
            total,
            onTime: onTime.length,
            late: late.length,
            cancelled: cancelled.length,
            onTimeRate,
            avgDeliveryTime,
        };
    }, [filteredOrders]);

    // Delivery status pie
    const statusPieData = [
        { name: 'On-time Delivery', value: metrics.onTime, color: '#10B981' },
        { name: 'Late Delivery', value: metrics.late, color: '#F59E0B' },
        { name: 'Cancelled Delivery', value: metrics.cancelled, color: '#EF4444' },
    ].filter(d => d.value > 0);

    // Delivery type bar
    const deliveryTypeSummary = useMemo(() => {
        const sameDay = filteredOrders.filter(o => o.deliveryType === 'Same Day');
        const nextDay = filteredOrders.filter(o => o.deliveryType === 'Next Day');
        const calcRow = (label, grp) => {
            const del = grp.filter(o => o.status !== 'Cancelled');
            const onTime = del.filter(o => getDeliveryStatus(o) === 'On Time');
            return {
                type: label,
                'On-time Delivery': onTime.length,
                'Late Delivery': del.length - onTime.length,
                'Cancelled Delivery': grp.filter(o => o.status === 'Cancelled').length,
            };
        };
        return [calcRow('Same Day', sameDay), calcRow('Next Day', nextDay)];
    }, [filteredOrders]);

    // Slot-wise bar chart data
    const slotChartData = useMemo(() => {
        const slotGroups = groupBy(filteredOrders, 'timeSlotId');
        return Object.entries(slotGroups)
            .filter(([slotId]) => slotId && slotId !== 'null' && slotId !== 'undefined')
            .map(([slotId, slotOrders]) => {
                const label = formatTimeSlot(slotId);
                const delivered = slotOrders.filter(o => o.status !== 'Cancelled');
                const onTime = delivered.filter(o => getDeliveryStatus(o) === 'On Time').length;
                const late = delivered.filter(o => getDeliveryStatus(o) === 'Late').length;
                const cancelled = slotOrders.filter(o => o.status === 'Cancelled').length;
                return {
                    slot: label,
                    'On-time Delivery': onTime,
                    'Late Delivery': late,
                    'Cancelled Delivery': cancelled,
                };
            })
            .sort((a, b) => a.slot.localeCompare(b.slot));
    }, [filteredOrders]);

    // City / Store table
    const cityStoreRows = useMemo(() => buildCityStoreRows(filteredOrders, getCityName), [filteredOrders]);

    // Time Slot table
    const timeSlotRows = useMemo(() => buildTimeSlotRows(filteredOrders), [filteredOrders]);

    // Raw export columns
    const rawColumns = [
        { key: 'id', label: 'Order ID', width: '180px' },
        { key: 'date', label: 'Date' },
        { key: 'storeName', label: 'Store' },
        { key: 'cityName', label: 'City' },
        { key: 'deliveryType', label: 'Type' },
        { key: 'timeSlotId', label: 'Time Slot', render: (v, row) => formatTimeSlot(row._original?.TimeSlotId || v) },
        { key: 'deliveryScheduleDate', label: 'Scheduled', render: v => v ? new Date(v).toLocaleDateString('en-IN') : 'N/A' },
        { key: 'pickUpDate', label: 'Picked Up', render: v => v ? new Date(v).toLocaleString('en-IN') : 'N/A' },
        { key: 'deliveryDate', label: 'Delivered', render: v => v ? new Date(v).toLocaleString('en-IN') : 'N/A' },
        { key: 'status', label: 'Status' },
        { key: 'isDelayed', label: 'Delayed?', render: v => v ? 'Yes' : 'No' },
        { key: 'total', label: 'Value', render: v => formatCurrency(v) },
        { key: 'cancelReason', label: 'Cancel Reason', render: v => v || '-' },
    ];

    // City/Store table columns
    const cityColumns = [
        { key: 'name', label: 'City / Store', width: '200px' },
        { key: 'totalOrders', label: 'Total Orders' },
        { key: 'onTimePercent', label: 'On-Time %', render: v => formatPercentage(v) },
        { key: 'latePercent', label: 'Late %', render: v => formatPercentage(v) },
        { key: 'cancelledPercent', label: 'Cancelled %', render: v => formatPercentage(v) },
        { key: 'avgDispatch', label: 'Avg Dispatch Time' },
    ];

    // Time slot table columns
    const slotColumns = [
        { key: 'timeSlot', label: 'Time Slot' },
        { key: 'totalOrders', label: 'Total Orders' },
        { key: 'onTime', label: 'On Time' },
        { key: 'late', label: 'Late' },
        { key: 'cancelled', label: 'Cancelled' },
        { key: 'onTimePercent', label: 'On-Time %', render: v => formatPercentage(v) },
    ];

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Delivery Performance</h1>
                <p className={styles.subtitle}>Real-time delivery SLA analysis based on live order data</p>
            </header>

            {/* KPI Cards */}
            <div className={styles.kpiGrid5}>
                <KPICard title="Total Orders" value={formatNumber(metrics.total)} icon={Truck} color="primary" />
                <KPICard title="On-time Delivery" value={formatNumber(metrics.onTime)} icon={CheckCircle} color="success" />
                <KPICard title="Late Delivery" value={formatNumber(metrics.late)} icon={Clock} color="warning" />
                <KPICard title="Cancelled Delivery" value={formatNumber(metrics.cancelled)} icon={XCircle} color="danger" />
                <KPICard
                    title="On-Time Rate"
                    value={`${metrics.onTimeRate.toFixed(1)}%`}
                    icon={TrendingUp}
                    color="info"
                />
            </div>

            {/* Charts Row */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <BarChart3 size={20} className={styles.sectionIcon} />
                    Delivery Status Breakdown
                </h2>
                <div className={styles.chartsGrid}>
                    {/* Pie Chart */}
                    <ChartCard title="Overall Status Mix" icon={CheckCircle} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={statusPieData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={3}
                                    label={({ name, value }) => `${name}: ${value}`}
                                    labelLine={false}
                                >
                                    {statusPieData.map((entry, i) => (
                                        <Cell key={i} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v) => formatNumber(v)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* Delivery Type Stacked Bar */}
                    <ChartCard title="On-Time vs Late by Delivery Type" icon={BarChart3} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={deliveryTypeSummary}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                <Legend />
                                <Bar dataKey="On-time Delivery" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="Late Delivery" stackId="a" fill="#F59E0B" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="Cancelled Delivery" stackId="a" fill="#EF4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>

            {/* Time Slot Performance */}
            {timeSlotRows.length > 0 && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>
                        <Calendar size={20} className={styles.sectionIcon} />
                        Delivery by Time Slot
                    </h2>

                    {/* Slot-wise stacked bar chart */}
                    {slotChartData.length > 0 && (
                        <ChartCard title="Slot-wise Delivery Breakdown" icon={BarChart3} height={280}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={slotChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                    <XAxis dataKey="slot" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }} />
                                    <Legend />
                                    <Bar dataKey="On-time Delivery" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="Late Delivery" stackId="a" fill="#F59E0B" radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="Cancelled Delivery" stackId="a" fill="#EF4444" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    )}

                    <DataTable
                        data={timeSlotRows}
                        columns={slotColumns}
                        title="Time Slot Performance"
                        icon={Clock}
                        exportFilename="slot_performance"
                        defaultPageSize={10}
                    />
                </section>
            )}

            {/* City / Store breakdown */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <MapPin size={20} className={styles.sectionIcon} />
                    City &amp; Store Performance
                </h2>
                {cityStoreRows.length > 0 ? (
                    <DataTable
                        data={cityStoreRows}
                        columns={cityColumns}
                        title="City & Store Delivery Performance"
                        icon={Store}
                        exportFilename="city_store_delivery"
                        defaultPageSize={10}
                    />
                ) : (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-tertiary)', background: 'var(--color-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
                        No delivery data available for the selected filters.
                    </div>
                )}
            </section>

            {/* Raw Export */}
            <section className={styles.section}>
                <DataTable
                    data={filteredOrders}
                    columns={rawColumns}
                    title="Raw Delivery Export"
                    icon={Truck}
                    exportFilename="delivery_raw_export"
                    defaultPageSize={10}
                    exportData={(data) => data.map(row => ({
                        ...row,
                        timeSlotId: formatTimeSlot(row._original?.TimeSlotId || row.timeSlotId),
                    }))}
                />
            </section>
        </div>
    );
}

export default DeliveryPerformance;
